import * as React from "react";
import { Controller, FormProvider } from "react-hook-form";
import {
  Card,
  ChipSelector,
  DatePicker,
  DatePickerHandle,
  Form,
  Modal,
  ModalRef,
  Text,
  Toggle,
  Tooltip,
} from "../../components";
import type { BookingViewModel } from "./viewModel";
import { AvailableSlots } from "./AvailableSlots";
import { BookingSummaryCard } from "./BookingSummaryCard";
import { BookingPaymentModalBody } from "./BookingPaymentModalBody";
import { SVGs } from "../../assets";
import { Tools } from "../../utils";
import { startOfDay } from "date-fns";

export function View(props: BookingViewModel) {
  const t = props.t;
  const paymentModalRef = React.useRef<ModalRef | null>(null);
  const customDatePopoverRef = React.useRef<DatePickerHandle | null>(null);

  type InfoCardSection = {
    title: string;
    fields: Parameters<typeof Form>[0]["fields"];
    titleEnd?: React.ReactNode;
  };

  function renderInfoCard(args: InfoCardSection | InfoCardSection[]) {
    const sections = Array.isArray(args) ? args : [args];
    return (
      <Card
        key={sections.map((s) => s.title).join("|")}
        className="bg-table-header space-y-[24px]"
      >
        {sections.map((section) => (
          <React.Fragment key={section.title}>
            <div className="flex items-center justify-between gap-[12px]">
              <Text level={4} className="font-[700]">
                {section.title}
              </Text>
              {section.titleEnd}
            </div>
            <Form form={props.form} fields={section.fields} />
          </React.Fragment>
        ))}
      </Card>
    );
  }

  return (
    <FormProvider {...props.form}>
      <div>
        <div className="mt-[16px] grid gap-[20px] lg:grid-cols-[1fr,380px] lg:items-start">
          <div className="flex flex-col gap-[24px]">
            <Text level={2}>
              {props.slots.length
                ? t("booking.dateTimeSelection")
                : t("booking.bookingDetails")}
            </Text>
            <Card className="border border-divider space-y-[16px]">
              <Controller
                control={props.form.control}
                name={"order.isGuestOrder"}
                render={({ field }) => (
                  <ChipSelector
                    value={field.value}
                    options={props.peopleOptions}
                    label={t("booking.howManyPeople")}
                    onChange={(v) => {
                      field.onChange(v);
                      props.onPeopleCountChange(v);
                    }}
                  />
                )}
              />
              {!!props.slots.length && (
                <>
                  <Controller
                    control={props.form.control}
                    name={"serviceDate"}
                    render={({ field }) => {
                      const isCustom =
                        field.value === "custom_date" ||
                        !props.dateOptions.some((o) => o.value === field.value);

                      const serviceDateValue = isCustom
                        ? "custom_date"
                        : field.value;

                      const options = [
                        ...props.dateOptions,
                        {
                          value: "custom_date",
                          icon: <SVGs.Calendar className="text-inherit" />,
                          label:
                            isCustom && field.value !== "custom_date"
                              ? Tools.formatYmd(field.value, "MMM d, yyyy")
                              : t("booking.customDate"),
                        },
                      ];

                      return (
                        <div className="relative">
                          <ChipSelector
                            required
                            value={serviceDateValue}
                            label={t("booking.chooseDate")}
                            options={options}
                            onChange={(v) => {
                              if (v === "custom_date") {
                                customDatePopoverRef.current?.open();
                                return;
                              }
                              customDatePopoverRef.current?.close();
                              field.onChange(v);
                            }}
                            onSelectedClick={(v) => {
                              if (v === "custom_date")
                                customDatePopoverRef.current?.open();
                            }}
                          />
                          <DatePicker
                            ref={customDatePopoverRef}
                            minDate={startOfDay(new Date()).toISOString()}
                          >
                            <DatePicker.Single
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </DatePicker>
                        </div>
                      );
                    }}
                  />
                  <AvailableSlots
                    t={t}
                    productId={props.product?.id ?? null}
                    timeZone={
                      props.location?.city?.name ??
                      props.product?.partner_location?.timeZone ??
                      props.location?.code
                    }
                    partnerProductLocationId={props.location?.id ?? null}
                    initialSlots={props.slots}
                  />
                </>
              )}
            </Card>
            {renderInfoCard(
              props.order_items.map((_, index) => ({
                title: `${t("general.memberInformation")} ${props.order_items.length > 1 ? `(${index + 1})` : ""}`,
                fields: props.getMemberFields(index),
                titleEnd: !index && (
                  <Controller
                    control={props.form.control}
                    name="order.sendVisitorBookingConfirmationEmail"
                    render={({ field }) => (
                      <div className="flex items-center gap-[10px]">
                        <div className="flex items-center gap-[4px]">
                          <Text>{t("general.notifyMember")}</Text>
                          <Tooltip
                            align="end"
                            position="top"
                            content={t("booking.notifyMemberTooltip")}
                          >
                            <img
                              src={SVGs.questionRound}
                              alt="info"
                              className="h-[14px] w-[14px] mt-[1px]"
                            />
                          </Tooltip>
                        </div>
                        <Toggle
                          value={field.value === 1}
                          onChange={(checked) =>
                            field.onChange(checked ? 1 : 0)
                          }
                        />
                      </div>
                    )}
                  />
                ),
              })),
            )}
            {renderInfoCard({
              title: t("booking.billingInformation"),
              fields: props.billingFields,
            })}
            {renderInfoCard({
              title: t("booking.bookerDetails"),
              fields: props.bookerFields,
            })}
          </div>
          <BookingSummaryCard
            t={t}
            paymentRequired={props.paymentRequired}
            summary={props.summary}
            onContinue={() => paymentModalRef.current?.open()}
          />
        </div>
        <Modal ref={paymentModalRef} title={t("booking.howPaymentHappens")}>
          <BookingPaymentModalBody
            t={t}
            paymentRequired={props.paymentRequired}
            summary={props.summary}
            onClose={() => paymentModalRef.current?.close()}
            onProceed={props.onProceed}
          />
        </Modal>
      </div>
    </FormProvider>
  );
}
